import { fetchJSON as fetchAPI } from './client';

export type ListingGridRow = {
  analysis_id: string;
  asin?: string;
  sku?: string;
  catalog_id?: string;
  title?: string;
  health_score: number;
  seo_score: number;
  compliance_score: number;
  rec_count: number;
  pending_review: number;
  prompt_version?: string;
  analyzed_at: string;
};

export type DashboardSummary = {
  tenant_id: string;
  workspace_id?: string;
  total_analyses: number;
  total_recommendations: number;
  pending_review: number;
  approved_count: number;
  applied_count: number;
  avg_health_score: number;
  avg_confidence: number;
  pending_bulk_jobs: number;
  unread_notifications: number;
  indexed_items: number;
  by_rec_status?: Record<string, number>;
  by_rec_type?: Record<string, number>;
  recent_activity?: ActivityEntry[];
  top_listings?: ListingGridRow[];
  generated_at: string;
};

export type AnalysisResult = {
  id: string;
  workspace_id: string;
  listing: {
    asin?: string;
    sku?: string;
    title?: string;
    bullets?: string[];
    description?: string;
    brand?: string;
    category?: string;
  };
  health: { overall_score: number; issues?: { field: string; message: string; severity: string }[] };
  seo: { keyword_coverage: number; suggested_keywords?: string[]; keyword_gaps?: string[] };
  quality: { overall_quality: number; readability: number };
  compliance: { overall_compliance: number; restricted_claims?: string[] };
  recommendations?: Recommendation[];
  prompt_version?: string;
  analyzed_at: string;
};

export type Recommendation = {
  id: string;
  type: string;
  status: string;
  current?: string;
  suggested: string;
  rationale?: string;
  confidence: number;
  prompt_version?: string;
};

export type SideBySideComparison = {
  field: string;
  current: string;
  suggested: string;
  recommendation_id?: string;
  confidence: number;
  explanation?: { summary: string; factors?: { factor: string; description: string; weight: number }[] };
};

export type OptimizationWorkflow = {
  id: string;
  analysis_id: string;
  state: string;
  publish_ready: boolean;
  steps: { name: string; state: string; message?: string }[];
};

export type SearchResult = {
  recommendations?: Recommendation[];
  total: number;
  facets?: { rec_status?: Record<string, number>; rec_type?: Record<string, number> };
  took_ms?: number;
};

export type ActivityEntry = {
  id: string;
  resource_id?: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type OptimizerNotification = {
  id: string;
  resource_id?: string;
  title: string;
  body?: string;
  priority: string;
  read: boolean;
  created_at: string;
};

export type BulkOperation = {
  id: string;
  action: string;
  state: string;
  analysis_ids?: string[];
  progress: number;
  processed: number;
  failed: number;
  total: number;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_AI_CATALOG_API ?? '/api/v1/ai/catalog';

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const aiCatalogApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<DashboardSummary>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  optimizerGrid: (workspaceId?: string, limit = 50, offset = 0) =>
    fetchJSON<{ rows: ListingGridRow[]; total: number }>(
      `/grid?workspace_id=${workspaceId ?? ''}&limit=${limit}&offset=${offset}`,
    ),
  analyze: (body: { workspace_id: string; listing: Record<string, unknown>; prompt_version?: string }) =>
    fetchJSON<AnalysisResult>('/analyze', { method: 'POST', body: JSON.stringify(body) }),
  getAnalysis: (id: string) => fetchJSON<AnalysisResult>(`/analyses/${id}`),
  globalSearch: (text: string, workspaceId?: string) =>
    fetchJSON<SearchResult>('/search/global', {
      method: 'POST',
      body: JSON.stringify({ text, workspace_id: workspaceId ?? '', limit: 50 }),
    }),
  advancedSearch: (query: Record<string, unknown>) =>
    fetchJSON<SearchResult>('/search/advanced', { method: 'POST', body: JSON.stringify(query) }),
  compare: (analysisId: string) => fetchJSON<SideBySideComparison[]>(`/analyses/${analysisId}/compare`),
  explain: (recId: string) =>
    fetchJSON<{ summary: string; factors?: { factor: string; description: string; weight: number }[] }>(
      `/recommendations/${recId}/explain`,
    ),
  workflow: (analysisId: string) => fetchJSON<OptimizationWorkflow>(`/analyses/${analysisId}/workflow`),
  transitionRecommendation: (id: string, status: string) =>
    fetchJSON<Recommendation>(`/recommendations/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
  submitFeedback: (body: { recommendation_id: string; analysis_id?: string; rating: string; comment?: string }) =>
    fetchJSON<{ id: string }>('/feedback', { method: 'POST', body: JSON.stringify(body) }),
  triggerBulk: (body: { action: string; analysis_ids: string[]; workspace_id: string }) =>
    fetchJSON<BulkOperation>('/bulk', { method: 'POST', body: JSON.stringify(body) }),
  listActivity: (resourceId?: string, limit = 50) =>
    fetchJSON<ActivityEntry[]>(`/activity${resourceId ? `?resource_id=${resourceId}&limit=${limit}` : `?limit=${limit}`}`),
  listNotifications: (unread = false) =>
    fetchJSON<OptimizerNotification[]>(`/notifications?unread=${unread}`),
  diagnostics: () => fetchJSON<Record<string, unknown>>('/diagnostics'),
};

export const BULK_ACTIONS = ['analyze', 'approve', 'apply', 'archive'] as const;
export const REC_STATUSES = ['draft', 'review', 'approved', 'rejected', 'applied', 'archived'] as const;
